<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        
        <transition name="fade-in-fast" mode="out-in">
            <div v-if="!formCanMount" key="initialLoading" class="d-flex align-items-center justify-content-center" style="min-height: 300px;">
                <busy-spinner></busy-spinner>
            </div>

            <div v-else key="formLoaded" class="">

                <div class="d-flex justify-content-between align-items-start">
                    <div class="w-65">
                        <h5>{{ title }}</h5>
                        <p class="text-muted">{{ instructions }}</p>
                    </div>
                    <div class="w-35 d-flex flex-column gap-2 align-items-end" style="">
                        <div class="w-100 d-flex gap-2 align-items-center justify-content-end">
                            <!-- <b-icon v-if="localIsValid === false" icon="record-fill" variant="danger" class="mr-3"></b-icon> -->
                            <b-icon v-if="hasChanges && !isSaveBehaviorButton && !submitting" icon="x-circle-fill" variant="warning"></b-icon>  <!-- showing this to watch how change detection is working, should eventually use it to block completion of task if there are changes -->
                            <b-badge v-if="hasChanges && isSaveBehaviorButton && !submitting" key="hasChangesBadge" variant="warning" class="text-uppercase">Unsaved Changes</b-badge>  <!-- showing this to watch how change detection is working, should eventually use it to block completion of task if there are changes -->
                            <b-spinner v-if="submitting" key="submitting" small class=""></b-spinner>
                            <b-badge v-else-if="savedShowing" key="saved" variant="success" class="text-uppercase">Saved</b-badge>
                            <!-- <b-badge v-else-if="hasChanges" key="unsaved" variant="soft-warning" class="text-uppercase">Unsaved</b-badge> -->
                            <!-- not showing unsaved to users... because it misses some changes -->
                            <b-badge v-if="isReadOnly" variant="secondary" class="text-uppercase">Read Only</b-badge>
                            <b-button variant="gray-secondary" size="sm" @click="refresh" class="" v-b-tooltip.hover title="Refresh Form">
                                <b-icon icon="arrow-clockwise" class=""></b-icon>
                            </b-button>
                        </div>
                        <div class="w-100 d-flex gap-2 align-items-center justify-content-end">
                            <!-- CHANGE HIGHLIGHT AREA - show different things based on save behavior -->
                            <div v-if="isSaveBehaviorButton && !isReadOnly" class="">
                                <!-- TODO: hide the normal hasChanges/submitting/saved indicators when this button is showing, have it do those things -->
                                <b-button
                                    :variant="hasChanges ? 'primary' : 'light-primary'"
                                    class=""
                                    size="sm"
                                    :disabled="!localIsValid"
                                    @click="handleSaveButton"
                                >
                                    <b-spinner v-if="loading || submitting" key="btnLoadingSpinner" small></b-spinner>
                                    <span v-else key="btnText">{{ localIsValid ? saveButtonText : invalidSaveButtonText }}</span>
                                </b-button>
                            </div>
                        </div>
                    </div>
                    
                </div>
                
                <FormulateForm
                    v-if="formCanMount"
                    ref="theForm"
                    :name="formName"
                    :debounce="100"
                    v-model="localFormData"
                    :schema="formSchema"
                    class="mt-2"
                    :class="formCssClasses"
                    @created="handleFormCreated"
                    @validation="handleFormValidation"
                    @blur="handleBlur"
                    @input="handleFormInput"
                    @submit="handleSave"
                    @submit-raw="handleSubmitRaw"
                    #default="{ isLoading, hasErrors }"
                >  
                    <div v-if="isSaveBehaviorButton && !isReadOnly" class="mt-3 w-100 d-flex justify-content-end align-items-end gap-2">
                        <div class="d-flex gap-4 align-items-center">
                            <!-- <div style="">
                                <b-spinner v-if="isLoading" small class=""></b-spinner>
                                <b-badge v-else-if="savedShowing" variant="success" class="text-uppercase">Saved</b-badge>
                            </div> -->
                            <b-button
                                :variant="hasChanges ? 'primary' : 'light-primary'"
                                class=""
                                size="lg"
                                :disabled="hasErrors"
                                @click="handleSaveButton"
                            >
                                <b-spinner v-if="isLoading" key="btnLoadingSpinner" small></b-spinner>
                                <span v-else key="btnText">{{ saveButtonText }}</span>
                            </b-button>
                        </div>
                    </div>

                    <FormReadySentinel
                        @ready="handleFormMounted"
                    ></FormReadySentinel>
                    <!-- <div>{{ isValid ? 'VALID' : 'not valid'}}</div> -->

                </FormulateForm>
                <div class="">
                    <!-- <pre>{{ localFormData }}</pre> -->
                </div>
            </div>
        </transition>

    </div>
</template>

<script>
import Vue from 'vue';
import { errorHandling } from '../../js/custom-vue-mixins';
import cloneDeep from 'lodash/cloneDeep';
import { 
    getFlowObjectApi
} from '../../js/api-service';
import debounce from 'lodash/debounce';
import isEqualWith from 'lodash/isEqualWith';
import { asyncSplFileSlinger } from '../../js/file-helpers';  // NEW VERSION


const FormReadySentinel = Vue.component('FormReadySentinel', {
    // a component that just emits something when it mounts which we put inside the FormulateForm slot so we can kinda get a mounted event for the form (since Formulate doesn't give us one)
    mounted() {
        this.$emit('ready');
    },
    template: `<span></span>`
});

export default {
    name: 'FlowNodeFormSingle',
    components: {
        FormReadySentinel,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        flowObjectClass: {
            type: String,
            required: true,
        },
        flowSubtype: {
            type: String,
            default: null,
        },
        flowObjectId: {
            type: String,
            required: true,
        },
        nodeKey: {
            type: String,
            required: true,
        },
        formKey: {  // just a key for each form on a task to keep things organized -- needed if they aren't each DB items
            // we use 'key' on backend for consistency/simplicity, but 'key' cannot be used as a prop, so have to pass it as formKey
            type: String,
            required: true,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            formCanMount: false,  // similar to loading... but starts off so form doesn't render first time until ready 
            formDidMount: false,  // set this true when your 'sentinel' component in the form slot mounts - then form is actually ready - and set false when you refresh
            loading: false,
            submitting: false,
            form: {},  // from API - includes title, instructions, items
            formSchema: [],  // set it explicitly here 
            localFormData: {},  // we get from API in mounted - separate from form object
            lastSavedData: '',  // stashed on last fetch/save for change detection
            localIsValid: null,  // must set this when we explicitly check form validation status 
            //canBackgroundSave: false,  // MOVED TO COMPUTED  set this when ready to start background save, and turn off before destroy?
            backgroundSaveDefault: 1000,
            showSavedTime: 1000,
            savedShowing: false,
            //fileCategory: 'quality_issues',   // no need... should get from field schema?
            hasChanges: null,  // this used to miss some things, but now improved as of March 30 2025, with better watching of nested changes -- but watch out for issues here...
        }
    },
    computed: {
        title() {
            return this.form?.title ?? '';
        },
        instructions() {
            return this.form?.instructions ?? '';
        },
        // schema() {
        //     return this.form?.items ?? [];
        // },
        formName() {
            // unique name based on task/form keys
            return `${this.nodeKey}${this.formKey}`;
        },
        isReadOnly() {  // NOTE: this is checked by the parent also!
            // this is set by backend, in this.form.read_only
            return this.form?.read_only === true;
        },
        // SAVE BEHAVIOR RELATED
        canBackgroundSave() {
            // added formCanMount?  make sure its not 'button' behavior (button)
            return this.formCanMount && this.isSaveBehaviorLive;
        },
        isSaveBehaviorLive() {
            return this.form?.save_behavior === 'live';
        },
        isSaveBehaviorSubmit() {
            // are we supposed to save everything on submit? based on save_behavior
            return this.form?.save_behavior === 'submit';
        },
        isSaveBehaviorButton() {
            return this.form?.save_behavior === 'button';  // should be 'live', 'button', or 'submit' - 'submit' means NO BUTTON, only save on the TASK/NODE submit
        },
        saveButtonText() {
            return this.form?.save_button_text ?? 'Save';
        },
        invalidSaveButtonText() {
            // just hard-coded locally for now -- need to make it obvious to user that they have to fix something before saving
            return 'Form Incomplete';
        },
        formCssClasses() {
            // from backend it will be a list of strings
            return this.form?.css_classes || [];
        },
        flowObjectApi() {
            return getFlowObjectApi(this.flowObjectClass, this.flowSubtype);
        },
    },
    // watch: {
    //     'localFormData': {  // this does not help group fields... they don't update local data... need to 'pull' it out of the form
    //         handler: function (newVal, oldVal) {
    //             console.log('HELLO FROM localFormData WATCH HANDLER - old -> new', oldVal, newVal);
    //             this.handleAnyFormChange();
    //         },
    //         deep: true,
    //     },
    // },
    created() {
        this.debouncedBackgroundSave = debounce(this.backgroundSave, this.backgroundSaveDefault);
    },
    mounted() {
        this.getForm();
    },
    beforeDestroy() {  // not sure if we need this... added it because we thought it was causing problems, but that was likely from bad handling of cancelled axios requests returning undefined
        //this.canBackgroundSave = false;  // moved to computed
        this.debouncedBackgroundSave.cancel();  // also has a .flush() method to invoke any waiting calls
    },
    methods: {
        // METHODS YOU CAN / INTEND TO CALL FROM PARENT 
        async refresh() {
            // trigger a refresh of form, just calls getForm() again 
            return await this.getForm();
        },
        async beforeNodeSubmit() {  // removed async
            // the PARENT TASK/NODE component calls this when the "SUBMIT" button was clicked on the TASK/NODE level
            // this MUST return PROMISE that resolves to BOOLEAN for whether or not the submit can proceed
            // ALSO... if the save_behavior was set to 'submit', then we execute the save here before returning the boolean
            //          because the task/node submit trigger does NOT also submit all the forms
            // BE AWARE: if you have lots of forms on one task/node, and all have save_behavior of 'submit', 
            //          many requests will get kicked off all at once, and not necessarily hit the backend in any specific order... which could cause problems with data being updated / race conditions
            //          if that happens, then maybe await each form submit in order, rather than Promise.all()
            // May 17 2023 - TODO even for save_behavior of 'live' or 'button' (all actually) we will check for any unsaved changes by going into state of form, to make sure we catch any unsaved changes on group fields, etc.
            console.log('starting beforeNodeSubmit in form ', this.title);
            let submitComplete = false;
            if (this.isSaveBehaviorSubmit) {
                const saveSuccess = await this.startSave();  // NOTE - this doesn't wait for form to actually submit!!!  (when we had await in front of it at least)
                console.log('save behavior is submit - saveSuccess is', saveSuccess);
                // TODO - that might still return true even if there were validation errors on the form and it aborted save... fix it
                submitComplete = saveSuccess;  // if save was NOT successful, we cancel the node submit
            } else if (this.isSaveBehaviorButton) {
                // if its button save, then maybe we could check for any unsaved changes and any isBusy status... 
                // then if has unsaved changes, show a warning to the user that its unsaved... 
                // TODO -- for now we'll just allow it to proceed
                // submitComplete = true;
                // CHANGED April 22 2025 -- was stupid to not save before submit here... should at least display warning to user... but its not clear to them what was saved vs. not saved... so we will just always save before submittng the task for completion
                // we will just repeat the logic from isSaveBehaviorSubmit above
                const saveSuccess = await this.startSave();
                console.log('save behavior is button - saving before completing task - saveSuccess is', saveSuccess);
                submitComplete = saveSuccess;  // if save was NOT successful, we cancel the node submit
                // TODO - you should also make it clear to user if there are MULTIPLE FORMS and they navigate away from this form, that their changes will not be saved... will get wiped away when we come back and reload the form....
            } else if (this.isSaveBehaviorLive) {
                // if 'live' save behavior, also check for any unsaved changes and isBusy status...
                // if has unsaved and not busy, then call startSave to save immediately, await it, then continue 
                // TODO - for now we'll allow it to proceed, make sure submit button on NODE is disabled if any of its forms are busy?
                // if currently busy, then just reject and show message to user saying 'wait a sec, try again'
                submitComplete = true;
            } else {  // just in case we forgot some condition...
                submitComplete = true;
            }
            // if submitComplete and all is valid, then we can return true, otherwise false
            // for validation... maybe we want to call the validation check method on the form itself, rather than relying on localIsValid? maybe...
            if (submitComplete && this.localIsValid) {
                return true;
            } else {
                console.log(`Cannot submit form_key ${this.formKey} - submitComplete? ${submitComplete} - isValid? ${this.localIsValid}`);
                return false;
            }
        },
        async beforeNavigateAway() {
            // call this FROM PARENT? before we navigate away from this single form -- such as in multi-page forms
            // do any final saving (if allowed based on save_behavior, such as 'live')
            // MUST RETURN BOOLEAN - false if CANNOT navigate away, true if CAN proceed - ALTHOUGH NOT CURRENTLY ENFORCED IN PARENT IN MOST PLACES (not sure how)
            // check for changes?  no need if we just call this.handleAnyFormChange() -- that checks for changes and does background save if needed
            if (this.isReadOnly) {
                return true;
            }
            // the handleAnyFormChange doesn't trigger save when save behavior is 'button' -- so we need to check for that here and save immediately if there are changes
            // ideally we should prompt the user and ask them if they want to save... maybe they didn't want to save their changes
            // but until we can sort that our, we'll trigger save here -- this is added April 29 2025
            if (this.isSaveBehaviorButton) {
                console.log('from beforeNavigateAway - save behavior is BUTTON, so we will save immediately using startSave() for formKey', this.formKey);
                const saveSuccess = await this.startSave();
                return saveSuccess;  // if save was NOT successful, we cancel the node submit
                // TODO - are all our return types correct here???
            } else {
                const saveImmediate = true;
                console.log('from beforeNavigateAway - calling handleAnyFormChange with immediate save on formKey', this.formKey);
                return this.handleAnyFormChange(saveImmediate);
            }
        },
        ///////////////////////////
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getForm() {
            this.loading = true;
            this.formCanMount = false;
            this.formDidMount = false;  // set true when our sentinel component mounts
            let resp;
            try {
                resp = await this.flowObjectApi.get_task_form(this.flowObjectId, this.nodeKey, this.formKey);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                this.handleApiResponse(resp.data);
                this.loading = false;
                this.formCanMount = true;
                //this.canBackgroundSave = true;  // moved to computed
            }
            // call handleAnyFormChange once, to report initial validation?
            //this.handleAnyFormChange(); // things to report might not be ready yet (like isValid)
            // doing handleAnyFormChange() here is not good... it makes the form 'dirty' which forces any group fields to run validation on the first empty item, then consider themselves invalid if anything is invalid
            // but that screws up allowing group fields to be empty if not required
            // so instead, we'll call method to check/emit validation status... but not actually submit once 
            // console.log('from end of getForm - will call reportValidation - formKey =', this.formKey);
            //this.reportValidation();  // NOPE - still don't have form $ref here
            // so instead we put the component inside the form, then in mounted() of that 'sentinel' component we have the form ref, emit validation then!
        },
        async prepForm() {
            // group fields are horrible when it comes to validation (and everything else)
            // 2.5.2 changed the 'default' value for group fields from [] to [{}]
            // vue formulate authors recommend just setting it back manually in some hook:  https://stackoverflow.com/questions/63366497/initially-hide-first-group-in-vue-formulate-repeatable-group
            // we'll do that here - I think it should be after the form mounts?
            // gotta go through schema recursively and find the group fields 
            await this.$nextTick();
            const isGroupField = (fld) => (fld.type === 'group');

            // // NEW STUFF BELOW -- it seems to be OK, and SHOULD handle nested group fields in a safer way, but still does not make up for how vue-formulate sets default on group fields to [{}] instead of []
            // // WE ARE KEEPING THESE FUNCTIONS, BUT COMMENTING OUT AS OF JUNE 3 2026
            // const setEmptyGroupField = (fieldName, parentDataObject) => {
            //     const curVal = parentDataObject[fieldName];
            //     if (Array.isArray(curVal) && curVal.length < 1) {
            //         // maybe also do this if curVal is null or undefined?  but maybe not needed, because it should be set to [] by default by vue-formulate?  but maybe not correctly?
            //         console.log(`setEmptyGroupField WILL SET VALUE OF ${fieldName} TO EMPTY ARRAY on object`, parentDataObject);
            //         this.$set(parentDataObject, fieldName, []);  // this is based on what justin at vue-formulate suggests... but using this.$set or else it is not reactive
            //     }
            // };

            // // Recursive function to traverse schema and reset empty group fields
            // // NOTE -- we really need to pay attention to data fields vs. non-data fields here... schema will have both it it!
            // const fixField = (field, parentData) => {
            //     // parentData should always be an object here... if we are working with arrays, then we need to call this on each item
            //     // const currentVal = parentData[field.name];
                
            //     if (isGroupField(field)) {
            //         console.log('found group field in fixField recursive function - field name is', field.name, ' - parentData is', parentData);
            //         if (parentData && Array.isArray(parentData)) {
            //             // if we are here, that means we have a NESTED GROUP FIELD -- a group field inside a repeatable
            //             // we will NOT support more than one level of nesting for group fields inside repeatables -- that would be just too much to handle with the way vue-formulate handles group fields and repeatables
            //             // we can support more levels once we are off of vue-formulate
            //             // if parent value is an array, then we need to check each item in the array for the value of this field, and set it to [] if its empty -- this is for nested group fields inside repeatables -- we have to check each one in the array!
            //             // we call this same function recursively, but with each object passed in instead of the array
            //             parentData.forEach((parentValueObj) => setEmptyGroupField(field.name, parentValueObj)); // or call THIS function recursively?
            //         } else if (parentData) {
            //             // if we have a parent value, and its not an array, then we can just check the value of this field directly on the parent value object
            //             setEmptyGroupField(field.name, parentData);
            //         }
            //     }
                
            //     // Recursively process children -- but only if we are not in an array, because if we are in an array, then we will call this function recursively on each item in the array, and we don't want to process children multiple times
            //     if (field.children && Array.isArray(field.children)) {
            //         let currentVal;  // undefined by default
            //         if (field.name && parentData && !Array.isArray(parentData)) {
            //             currentVal = parentData[field.name];  // could be multiple types... array, object, primitive, etc.
            //         }                    
            //         field.children.forEach(cf => fixField(cf, currentVal));
            //     }
            // };

            // // TODO - use this new version when fully tested / ready
            // // Apply recursive function to all top-level schema items
            // this.formSchema.forEach(sch => fixField(sch, this.localFormData));


            /// OLD VERSION BELOW -- but not recursive!
            const groupFieldNames = [];
            const groupFieldPaths = [];  // array of path arrays (2D array!) so we don't mix things up with dotted paths in field names
            const checkField = (field, parentPath) => {
                // parent path is the DATA PARENT path, if there is one -- could be empty string / undefined for top-level fields
                const currentPath = parentPath ? [...parentPath] : [];
                if (field.name) {
                    currentPath.push(field.name);
                }
                if (isGroupField(field)) {
                    console.log('found group field in checkField recursive function - field name is', field.name, ' - currentPath is', currentPath);
                    groupFieldNames.push(field.name);
                    groupFieldPaths.push(currentPath);
                    // TODO - this is not good... because this is recursive, and children fields will not have their parent as a prefix... so we merge all children of group fields together with top-level fields!  could create conflicts!
                    // we might want to build "composite" field names here, like 'parent.child', at least locally here where we override the localFormData for certain fields...
                    // actually, instead of just collecting the field names, we can just get the value from localFormData right here and mutate the value right here as we are already recursing through (avoids the need for duplicate recursion)
                    // that's what the new stuff below/above is trying to do... but not fully tested yet
                }
                if (field.children && Array.isArray(field.children)) {
                    field.children.forEach(cf => checkField(cf, currentPath));
                }
            };
            this.formSchema.forEach(sch => checkField(sch));
            //console.log('GGGGGGGGGGGGGGGGGGGGGGGG groupFields are', groupFieldNames);
            // now see if those fields have a value of [] -- if so, set the value again explicitly, which will? for form to re-render?
            // stuff below only works for top-level fields
            // const emptyValues = [null, []];  // don't include undefined here 
            if (groupFieldNames.length > 0) {
                console.log(`checking these group fields for empty values and resetting to empty array if needed - field names, field paths:`, groupFieldNames, groupFieldPaths);
                // console.log('dumping formSchema and localFormData before resetting group fields');
                // console.log(JSON.stringify(this.formSchema, null, 2));
                // console.log(JSON.stringify(this.localFormData, null, 2));
            }
            // TODO - make this work for nested group fields as well -- it does not currently, but it won't unless you look at the PATH instead of just the field name
            // at least this is safer than what we had before, which just looked at top-level field names and could mess with nested group fields that had the same name as a top-level field -- now we have to look at the PATH to know which one to set to [] if empty
            groupFieldPaths.forEach(gfp => {
                if (Array.isArray(gfp) && gfp.length !== 1) {
                    console.warn('group field path has more than one segment, which means we have a nested group field, which is not fully supported by this code yet - path is', gfp);
                    return;
                }
                const topLevelFieldName = gfp[0];
                const localVal = this.localFormData[topLevelFieldName];
                //console.log(`localFormData VALUE of groupFieldName ${topLevelFieldName} is`, localVal);
                // this seems to honor the group having a 'minimum' set -- if minimum: 1 for example, it won't remove the value... which is good actually
                if (Array.isArray(localVal) && localVal.length < 1) {
                    //console.log(`WILL SET VALUE OF ${topLevelFieldName} TO EMPTY ARRAY`);
                    this.$set(this.localFormData, topLevelFieldName, []);  // this is based on what justin at vue-formulate suggests... but using this.$set or else it is not reactive
                }
            })

            // const groupFieldNames = [];
            // const groupFieldPaths = [];  // full dotted paths to these group fields -- if they are nested in array, we need to set each one in the array!
            // const checkField = (field, parentDottedPath, parentValue) => {
            //     const pathSegments = [];
            //     if (parentDottedPath) {
            //         pathSegments.push(parentDottedPath);
            //     }
            //     if (field.name) {
            //         pathSegments.push(field.name);
            //     }
            //     const currentPath = pathSegments.join('.');
            //     // TODO - gotta handle parentValue being an array!
            //     const currentVal = parentValue ? parentValue[field.name] : undefined;   // could be multiple types... array, object, primitive, etc.
            //     if (isGroupField(field)) {
            //         groupFieldNames.push(field.name);
            //         groupFieldPaths.push(currentPath);
            //         // TODO - this is not good... because this is recursive, and children fields will not have their parent as a prefix... so we merge all children of group fields together with top-level fields!  could create conflicts!
            //         // we might want to build "composite" field names here, like 'parent.child', at least locally here where we override the localFormData for certain fields...
            //         // actually, instead of just collecting the field names, we can just get the value from localFormData right here and mutate the value right here as we are already recursing through (avoids the need for duplicate recursion)
            //         // likely an array if we have a value, but could be undefined if field is not present or no parentVal?
            //         if (Array.isArray(parentValue)) {
            //             // if we are here, that means we have a NESTED GROUP FIELD -- a group field inside a repeatable
            //             // we will NOT support more than one level of nesting for group fields inside repeatables -- that would be just too much to handle with the way vue-formulate handles group fields and repeatables
            //             // we can support more levels once we are off of vue-formulate
            //             // if parent value is an array, then we need to check each item in the array for the value of this field, and set it to [] if its empty -- this is for nested group fields inside repeatables -- we have to check each one in the array!
            //             parentValue.forEach((parentValueObj) => setEmptyGroupField(field.name, parentValueObj));
            //         } else if (parentValue) {
            //             // if we have a parent value, and its not an array, then we can just check the value of this field directly on the parent value object
            //             setEmptyGroupField(field.name, parentValue);
            //         }
            //         // if value is not an array, then maybe we should set value to be [] also???? TBD... maybe
            //         // if value is an array but non-zero length, we don't need to do anything HERE because we don't need to do the trick of forcing array to be empty
            //     }
            //     if (field.children && Array.isArray(field.children)) {
            //         field.children.forEach(cf => checkField(cf, currentPath, currentVal));
            //     }
            // };
            // this.formSchema.forEach(sch => checkField(sch));  // no parent path on root level fields
            // //console.log('GGGGGGGGGGGGGGGGGGGGGGGG groupFields are', groupFieldNames);
            // // now see if those fields have a value of [] -- if so, set the value again explicitly, which will? for form to re-render?
            // // stuff below only works for top-level fields
            // // const emptyValues = [null, []];  // don't include undefined here 
            // if (groupFieldNames.length > 0) {
            //     console.log(`checking these group fields for empty values and resetting to empty array if needed - field names, field paths:`, groupFieldNames, groupFieldPaths);
            //     console.log('dumping localFormData before resetting group fields');
            //     console.log(JSON.stringify(this.localFormData, null, 2));
            // }
            // WE MOVED ABOVE TO HANDLE IT IN THE SAME RECURSION, so we can check the value and reset it right there
            // groupFieldNames.forEach(gfn => {
            //     const localVal = this.localFormData[gfn];
            //     //console.log(`localFormData VALUE of groupFieldName ${gfn} is`, localVal);
            //     // this seems to honor the group having a 'minimum' set -- if minimum: 1 for example, it won't remove the value... which is good actually
            //     if (Array.isArray(localVal) && localVal.length < 1) {
            //         console.log(`WILL SET VALUE OF ${gfn} TO EMPTY ARRAY`);
            //         this.$set(this.localFormData, gfn, []);  // this is based on what justin at vue-formulate suggests... but using this.$set or else it is not reactive
            //     }
            // })
        },
        handleApiResponse(apiData) {
            // handles same return for get/submit
            //console.log('api response data', apiData);
            this.localFormData = apiData.data;
            this.form = apiData.form;  // TODO - need to prep the schema, pass in the file upload function 
            const schema = cloneDeep(this.form.items);
            schema.forEach(sch => this.prepSchemaItem(sch));  // just mutates in place - don't set it on this.formSchema until we're done
            this.formSchema = schema;
            // OLD VERSION this.lastSavedData = this.makeChangeDetectionString(apiData.data);
            this.lastSavedData = cloneDeep(apiData.data);
        },
        prepSchemaItem(item, parent = null) {
            // recursive function to handle any modifications to schema before setting it on the form 
            // need to at least attach file upload function to any 'file' inputs
            // just mutates the schema in place, not a copy - so make a copy before hand
            //console.log('prepping schema item', item);
            // should always have item, but not always name (for layout components, etc.)
            // NOTE: this is only the schema, for repeatables, it will only have ONE child... not one for each item in data! remember that...
            const itemName = item?.name ?? ''; 
            const parentName = parent?.name ?? '';
            const fullPath = parentName ? `${parentName}.${itemName}` : itemName;
            if (itemName) {
                console.log('prepping schema item with fullPath:', fullPath);
            }
            if (item && item.type && item.type === 'file') {
                const parentId = item.parentId;  // the parent in DB, not in the form
                const fileCategory = item.fileCategory;  // added these in backend when making field schema
                item.uploader = this.getFileUploader(fileCategory, null, parentId);
            }
            // also set disableErrors=true if a field is disabled -- vue-formulate doesn't do that for all rules, and its annoying
            if (item.disabled && item.name) {
                // only do this on components that have 'name' - so not layout components, etc.
                item.disableErrors = true;
            }
            if (itemName) {
                // TODO - make a nested path (or unique ID or use vue-formulate unique ID) instead of just the name... so we can track changes on nested fields
                // but this only applies to the schema... and for repeatables, all children in repeatable have the same schema/name....
                // but this is a GREAT way to track changes to all components in the form!  at least know when a change happens
                item['@input'] = (e) => this.handleComponentInput(fullPath, e);
                // YES: blur-context seems to work for all components, even nested ones... and event payload is the entire component context object, so we know LOTS about it (value, errors, )
                item['@blur-context'] = (e) => this.handleChildBlurContext(fullPath, e);  // might not need this if you have @blur=true on all components, which gets handled by handler on the form, which then calls the handler
            }
            // syntax is that @input (component event) is handled by 'some-custom-event' event listener on the form
            // you could also just pass in the function here -- that might be better, then you can pass in more info about each component
            // TODO - also, be smarter for what even to listen to on each component, based on type -- you can just have a lookup table:  input, change, blur, custom, etc.
            // then possibly turn off (or reduce or coordinate) the debounce of each input and the debounce in your change detection 
            
            // TODO TODO TODO - yes, do this ^^^^
            // rewrite this from scratch to better handle save behaviors (submit, button, etc. and showing unsaved changes)
            // when you save, capture snapshot of what was saved, and record changes since last save
            // then when stuff comes back from backend, merge what was changed since last save into new stuff from backend... it should override
            // how to track what was changed?  maybe just handle top-level fields?  track paths of changes, when you attach the change listener?  
            // you could also timestamp things, and/or keep an array of changes (event stream?) 
            // also, set different debounces for each input in here, based on type, etc.
            // also, set different listeners for each input, based on type, etc. - one for capturing changes, one for triggering save (like blur)
            // or just have a special listener on all components (need all custom components for this) and emit their path in data also...
            // TODO - have to ignore disabled fields in child input/blur context... they may get sent through

            
            if (item.children && Array.isArray(item.children)) {
                // for text components, we still have item.children, but its just text, not array of nested components/elements
                item.children.forEach(ch => this.prepSchemaItem(ch, item));
            }
        },  
        handleComponentInput(fullPath, value) {
            console.log(`###  COMPONENT INPUT - ${this.formKey} ###`, fullPath, value);
            //this.handleAnyFormChange();
            // NOTE: calling handleAnyFormChange() here is MUCH better for catching all changes, but then we have the "live save" problem on repeatables too -- previously they only saved on blur, with this they trigger on all inputs
            // you should have super sensitive change detection, but only save on blurs, or whatever the "I'm done" event is for each field type (could be input/change on some)
            this.checkDataChanged();  // for now just triggering our change detection... then do something with that (block submit, set timeout for saving?)
            // TODO - NOT DOING MORE WITH THIS NOW, because we need to rewrite the whole thing to handle save behaviors better
        },
        handleChildBlurContext(fullPath, componentContext) {
            const isInGroup = componentContext ? componentContext.isSubField() : false;  // if it has a dot, its in a group field
            console.log(`###  CHILD BLUR CONTEXT - ${this.formKey} ###`, fullPath, isInGroup, componentContext);
        },
        handleFormInput(payload) {
            // this is called from FORM @input event, which doesn't capture all things -- misses stuff in repeatables, etc.
            // this can/should be replaced by the 'some-custom-event' event listener on the form, which captures all changes, or direct event handler on all component inputs
            // but you shoud split out change detection and save triggers before replacing this, since triggering on ALL child updates will make jankiness worse
            console.log(`==================== FROM handleFormInput - ${this.formKey} - payload is`, payload);
            this.handleAnyFormChange();
        },
        handleBlur() {
            // console.log('Hello from handleBlur on FlowNodeFormSingle');
            // NOTE: March 30 2025 -- this is getting called before data in groups (at least) is getting updated -- likely because of the debounce we have on all inputs (set on form level)
            // so should we debounce here also?  then you'll have so many debounces... will go crazy...?
            // if you include  { '@blur': true } in field schema, it gets picked up here
            // but requires you to use blurContext (context.blurHandler?) in custom components...
            this.handleAnyFormChange();
            // paylod is just the blur event - console.log('handleBlur', payload);
        },
        handleFormValidation() {
            // console.log('starting this.handleFormValidation');
            // FormulateForm emits a @validation event, which is just a relay of @validation from a single field
            // it is NOT the overall validation status for the form
            // BUT, we use that to know it is done running validation on some update
            // so we use this as a trigger point to go get the overall validation status and report it up to parent 
            // also have to trigger on any change so we catch initial stuff
            // SEE NOTE BELOW - this.reportValidation();
            // May 16 2023 -- should this just call handleAnyFormChange(), which also calls this.reportValidation()?  
            //   this gets called on group field updates (because @validation event first on formulate form) and we might need to submit
            // Note: March 30 2025 - this seems to never get called...
            this.handleAnyFormChange();
        },
        checkDataChanged() {
            console.log('starting checkDataChanged in form with formKey', this.formKey);

            // TODO - tenantuserpicker (maybe only in remote fetching mode? maybe only when setting default contacts) will cause initial data to look "changed"
            
            // NOTE: parent component (FlowNodeForms) can call this directly... maybe at certain times you don't expect?  Watch for that... 
            //       we might have to be defensive in here about aborting or returning early if things are not ready to be checked yet
            //       yes.... if form is not mounted yet, or otherwise cannot get the form ref or its internal data, then currentData will be undefined
            //       in those cases, we will just return false (not changed) to avoid triggering any unwanted behavior... 
            //       but we should be careful about when parent calls this, and maybe add some console warnings if we call it at weird times where form is not ready, etc.
            //       and also think about whether we should set this.hasChanges to false in those cases, or just leave it as null or something else to indicate 'not ready to check'

            const notReadyYet = this.$refs.theForm?.formContext?.value === undefined;  // self-documenting variable names!
            if (notReadyYet) {
                const hasForm = !!this.$refs.theForm;  // just to check if we even have the form ref yet... we do not... that is what causes this to be undefined
                console.log(`checkDataChanged called on formKey ${this.formKey} but form is not ready yet (hasForm? ${hasForm}) - cannot get current data from form context - returning false for hasChanges, and not updating this.hasChanges`);
                return false;
            }

            // NOTE: we need to account for computed things from backend coming back different, triggering another update  (unique tokens for download URLs, etc.)
            // need to only look at the things ON the form
            // NOTE - this does not work for FILE fields.  because the download_url changes on EACH update, so if we post data, then get new data back, we get a new URL (new security hash)
            // for those, maybe have to pass IDs only, then fetch file details from inside the component?
            // TODO - skip this altogether in certain cases.... like if we are read-only, if its a start node (no live data saving there), etc.
            // const perfomanceNow = performance.now();
            const currentData = cloneDeep(this.$refs.theForm?.formContext?.value);
            const lastData = cloneDeep(this.lastSavedData);
            //console.log('currentData:', JSON.stringify(currentData));
            //console.log('lastData:', JSON.stringify(lastData));

            const hasChanges = !isEqualWith(currentData, lastData, (objValue, othValue, key) => {
                // we can use this to ignore certain properties that don't affect the 'changed' status
                const ignoreKeys = ['download_url'];
                if (ignoreKeys.includes(key)) {
                    return true; // ignore this key
                }
                // NEW April 7 2026 -- for many fields, the backend sends back null, but then as soon as vue-formulate touches it (even if not actually touched, but just triggering error checking) it turns into an empty string
                // so we will consider those the same.... ideally we would only do this for certain field types.... can figure that out later
                // if this causes problems, then make another looser 'hasChanges'-ish thing for specific purposes... then look at all changes wholistically (like are there any changes other than just null vs. empty string changes) to determine if we show 'unsaved changes' or trigger saves, etc.
                if ((objValue === null && othValue === '') || (objValue === '' && othValue === null)) {
                    console.warn(`treating null and empty string as the same for key ${key} in change detection`);
                    return true; // consider null and empty string the same for change detection
                }
                return undefined; // returning undefined tells isEqualWith to use its default comparison for this key
            });
            console.log(`checkDataChanged - hasChanges?`, hasChanges);
            // console.log('currentData -> lastSavedData', currentData, lastData);
            this.hasChanges = hasChanges;
            return hasChanges;
        },
        makeChangeDetectionString(data) {
            // common utility function for dumping current values to a string for change detection
            // we use a replacer function in JSON.stringify() to ignore some properties that don't affect changed data - like 'download_url' in our S3 file objects
            const replacer = (key, value) => {  // also has args value, and the object being serialized (its recursive)
                if (key === 'download_url') {
                    return '';  // just replace VALUE with empty string 
                }
                return value;
            }
            return JSON.stringify(data, replacer);
        },
        async handleSubmitRaw(submitRawData) {
            console.log('hello from handleSubmitRaw', submitRawData);
        },
        async handleSave(submitData) {  // the handler for the form @submit event -- actually do it, rerun promise 
            //console.log(submitData);
            console.log(`starting the actual formulate submit (handleSave) on node_key ${this.nodeKey} and form_key ${this.formKey}`);
            if (this.isReadOnly) {
                console.warn('tried to submit but form is read only');
                return;
            }
            this.submitting = true;
            this.$emit('busy', true);  // tell parent we are busy to lock the submit button
            const postData = {
                node_key: this.nodeKey,
                form_key: this.formKey,
                data: submitData,
            };
            let resp;
            try {
                resp = await this.flowObjectApi.submit_task_form_data(this.flowObjectId, postData);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            console.log(`DONE with actual formulate submit (handleSave) on node_key ${this.nodeKey} and form_key ${this.formKey}`);
            if (resp?.data) {
                //console.log('Response data after submit: ', resp.data);
                this.handleApiResponse(resp.data);
                this.submitting = false;
                this.$emit('busy', false);  // tell parent we are NOT busy to unlock the submit button
                this.showSaved();
                this.checkDataChanged();  // updates this.hasChanges
                // EMIT TO PARENT - and have it merge in new data?
                this.$emit('submitted', cloneDeep(submitData));
            } else {
                // still set some things false
                this.submitting = false;
                this.checkDataChanged();  // updates this.hasChanges
                this.$emit('busy', false);  // tell parent we are NOT busy to unlock the submit button
            }
        },
        getFileUploader(fileCategory, title, parentId) {
            // this is a closure around the actual async file upload function 
            // that has certain things baked in
            const uploader = function (file, progressHandler, errorHandler, options) {
                return asyncSplFileSlinger(fileCategory, file, progressHandler, errorHandler, options, title, parentId);
            }
            return uploader;
        },
        // async fileUploader(file, progress, error, options) {
        //     // MUST use this signature for args - formulate expects it 
        // },
        showSaved() {
            this.savedShowing = true;
            setTimeout(() => {
                this.savedShowing = false;
            }, this.showSavedTime);
        },
        async handleSaveButton() {
            // do the submit manually, not background/live submit 
            await this.startSave();
        },
        async backgroundSave() {  
            // submit if things are valid, debounced version created in created() hook - this.debouncedBackgroundSave()
            if (!this.canBackgroundSave) {
                console.log('Cannot background submit');
                return;  
            }
            console.log('Should background submit now');
            return await this.startSave(); 
        },
        async saveNowIfNeeded() {
            // call this (and await it) just before you complete something, change page, etc... to force saving if needed, based on actual data in form, not only things that got bubbled up in @input events
            // does not check if canBackgroundSave (based on save behavior live/submit/button) or if form is mounted... should we?
            // if we are OK with only doing this if save behavior is live, then we can juse use handleAnyFormChange(immediate=true);
            //const currentData = cloneDeep(this.$refs.theForm?.formContext?.value);  // maybe this is async??? we seem to miss changes.... maybe because debounce?
            const isChanged = this.checkDataChanged();  // we get currentData in there
            console.log(`from saveNowIfNeeded on formKey ${this.formKey} - isChanged?`, isChanged);
            // need to trigger validation here, AND after form is DONE validating
            this.reportValidation();
            let saveSuccess;
            if (isChanged) {
                saveSuccess = await this.startSave();
            }
            return saveSuccess;
        },
        async startSave() {  // removed async
            // trying to avoid errors on tab navigation... we'll check if we still have form with this name before calling this? or just wrap in try/catch
            // NOTE - this runs the risk of not saving something if needed... but we also have lots of false save triggers on initial load, which is what we're trying to avoid - but maybe can find a better way to do that
            // putting this in try/catch... should only catch something here if we can't find the form or something...
            // but if there was an error in the actual submit, that should be caught in the submit handler itself, not make its way up here
            // NOTE - May 3 2023 - this does NOT wait for actual submission process!!!  (May 16 -- actually, I think it does now that we directly call formSubmitted)
            let success;  // need this for doing isSaveBehaviorSubmit - maybe use other places too... or just let exception get passed up...
            try {
                //console.log('calling this.$formulate.submit on formName', this.formName);
                //this.$formulate.submit(this.formName);  // THIS IS NOT ASYNC!  it just triggers the submission, but no await on it, no return value (undefined) 
                // Instead of that... we just call .formSubmitted() on the form component directly -- that's all $formulate.submit() does anyways!
                const submitReturn = await this.$refs.theForm.formSubmitted();
                console.log(`dumping submitReturn which is type ${typeof submitReturn}`, submitReturn);
                success = true;
            } catch (error) {
                console.warn(`Got error trying to call submit on formulate form ${this.formName} - maybe we navigated away`);
                console.warn(error);
                success = false;
            }
            console.log('done with calling this.$formulate.submit on formName', this.formName);
            // TODO - if there are validation errors on the form, we might not know here, still set success true!
            //      - figure out if there is any return value from $formulate.submit() or other way to know if it ACTUALLY submitted
            return success;
        },
        handleFormCreated(formComponent) {
            // payload is the Vue component of the FormulateForm itself
            const payload = {
                component: formComponent,
                key: this.formKey,
            };
            console.log(`FORM CREATED ${this.formKey} created - will emit ready payload:`, payload);
            console.log(`Do we have theForm ref for ${this.formKey}?`, Boolean(this.$refs['theForm']));  
            // this is annoying - we get the form @created event, but the ref is not ready yet... because it is emitted in FormulateForm created() lifecycle hook... we need later than that.
            // registration works OK, but we need to then report initial validation... but we don't have other events to trigger from...
            this.$emit('ready', payload);
        },
        async handleAnyFormChange(immediate = false) {
            // added immediate to call save immediately if needed, instead of debounced
            //      use this if saving right before we change pages, submit task, etc.
            //      make another method for saveNowIfNeeded() if needed, wait for it to complete?  or just addd that here...

            // NOTE - THIS DOES NOT GET CALLED WHEN YOU EXPECT!
            // FormulateForm @input event does not get fired when it should....
            // AND v-model with form data does not stay up to date
            // but I believe the payload from @submit event DOES contain the latest data for form...
            // so you need to check that and abort if nothing changed...
            // call the debounced bg submit - if there is a real change
            // also call this on all input @blur events?  
            // WE MUST PULL DATA FROM FORM - IT DOES NOT UPDATE ITSELF WELL WHEN WE HAVE GROUP FIELDS!
            // YES, we are MISSING changes -- still getting stale, previous values 
            //const currentData = cloneDeep(this.$refs.theForm?.formContext?.value);  // maybe this is async??? we seem to miss changes.... maybe because debounce?
            console.log('starting handleAnyFormChange on formKey', this.formKey, 'with immediate save?', immediate);
            const isChanged = this.checkDataChanged();  // we get currentData in there
            // NOTE: we changed checkDataChanged in April 2026 to return false if form is not ready yet... 
            //       that COULD introduct subtle issues? where we were previously triggering saves but we are not anymore because we are returning false until form is ready
            //       but this seems more correct and there should not be actual user input that causes changes before form is ready anyways
            console.log(`from handleAnyFormChange on formKey ${this.formKey} - isChanged?`, isChanged);
            // need to trigger validation here, AND after form is DONE validating
            await this.reportValidation();
            if (isChanged && this.canBackgroundSave) {
                if (immediate) {
                    console.log('will call backgroundSave - immediate is true');
                    return await this.backgroundSave();  // returns boolean
                } else {
                    console.log('will call debouncedBackgroundSave');
                    const debouncedResult = await this.debouncedBackgroundSave();  // returns boolean (returns result of most recent call to backgroundSave)
                    console.log('debounced background save complete with result', debouncedResult);
                    return debouncedResult;
                }
            } else {
                console.log(`isChanged? ${isChanged} - this.canBackgroundSave? ${this.canBackgroundSave}`);
                return true;
            }
        },
        async reportValidation() {
            // called when there are changes here... we tell parent our validation status
            // need data too??? maybe.... but try not to... just complicates things 
            // changed to only emit if we actually HAVE theForm - or else we can't really trust it... 
            const theForm = this.$refs['theForm'];
            // THIS WAY IS USUALLY NOT CORRECT - const isValid = theForm ? theForm.isValid : null;
            // call this method instead, returns a promise
            // TODO - add hasChanges to this?
            if (theForm && this.formCanMount && this.formDidMount) {
                const hasErrors = await theForm.hasValidationErrors();  // this may be what causes the 'jitter' when we first start entering data on first input
                console.log(`ON FORM ${this.formKey} ASYNC HAS VALIDATION ERRORS RESOLVED TO`, hasErrors);
                const isValid = !hasErrors;
                this.localIsValid = isValid;  // so we have it locally too
                console.log('********* we DO HAVE theForm', this.formKey, 'and isValid is', isValid);
                // only emit the result here
                this.$emit('is-valid', isValid);
            } else {
                console.log('we DO NOT HAVE theForm or form is not ready', this.formKey, 'so cannot check or report validation');
            }
        },
        handleFormEvent(evt) {
            console.log('generic form event', evt);
        },
        async handleFormMounted() {
            console.log('-----------------form mounted in our custom component', this.formKey);
            await this.prepForm();  // seems we  have to wait to do this until form actually mounts
            await this.$nextTick();
            this.formDidMount = true;
            this.reportValidation();  // yes!  this works!  seems we now have the ref for actually reporting validation!
        },

    }
}
</script>

<style lang="scss">

// NOTE - you are purposely allowing this to affect all things project-wide that have this class
// for example:  it is used in the flow starter also

.spl-flowform-input {

    $flowform-input-text-color: #000;
    $flowform-input-font-size: 14px;
    $flowform-input-line-height: 18px;
    $flowform-input-padding-vertical: 8px;
    $flowform-input-padding-horizontal: 8px;
    $flowform-input-background-color-disabled: rgba(0,0,0,0.05);
    $flowform-input-text-color-disabled: $flowform-input-text-color;
    $flowform-input-border-color: #ced4da;  // this is standard for PrimeVue it seems.... we'll use it as standard

    font-size: $flowform-input-font-size;
    color: $flowform-input-text-color !important;

    .form-control {
        // for any bootstrap inputs we use in here 
        border-color: $flowform-input-border-color;
        box-shadow: none;  // remove it here... looks different than others

        &.b-form-tags {
            &.disabled {
                background-color: $flowform-input-background-color-disabled;
                border-color: transparent;
                .b-form-tag {
                    color: $flowform-input-text-color-disabled;
                    opacity: 1;  // normally b-form-tag disabled is 0.75
                }
            }
        }

    }

    &.formulate-input {
        margin-bottom: 0.5rem;
    }

    .p-inputtext, .p-dropdown {
        // primevue dropdowns and such
        color: $flowform-input-text-color;
        font-size: $flowform-input-font-size;
        line-height: $flowform-input-line-height;
        &:disabled,
        &.p-disabled {
            opacity: 1;  // primevue stuff normall goes to 0.6 when disabled
            border-color: transparent;
            background-color: $flowform-input-background-color-disabled;
            color: $flowform-input-text-color-disabled;
        }
    }

    .formulate-input-element input,
    .formulate-input-element textarea {
        line-height: $flowform-input-line-height;  // also to match primevue
        padding-top: $flowform-input-padding-vertical;  // to match padding of prime-vue components, and it looks nicer, especially since we made fonts bigger
        padding-bottom: $flowform-input-padding-vertical;
        padding-left: $flowform-input-padding-horizontal;
        padding-right: $flowform-input-padding-horizontal;
        
        &:disabled {
            border-color: transparent;
            //padding-left: 0;
            //padding-right: 0;
            background-color: $flowform-input-background-color-disabled;
            color: $flowform-input-text-color-disabled;
        }
    }

    .formulate-input-element[data-type=advancedselect] {
        .p-disabled {  // this is on the PrimeVue outer wrapper
            background-color: $flowform-input-background-color-disabled;
            color: $flowform-input-text-color-disabled;
            border-color: transparent;
            opacity: 1;  // they normally set opacity to 0.6 for disabled
            .p-dropdown-trigger-icon,
            .p-dropdown-clear-icon {
                display: none;
            }
        }
        .p-multiselect-chip.p-disabled {
            .p-multiselect-token {  // the chips themselves
                color: #000;
                background-color: rgba(255,255,255,0.4);  // lighten it, background will be gray
            }
        }
    }

    .formulate-input-label {
        font-size: 0.8rem;
        font-weight: 500;
        //text-transform: uppercase;
        //color: var(--secondary);
    }

    // TOP LEVEL OF GROUPS
    &[data-type=group] {
        .formulate-input-group-add-more button {
            background-color: var(--primary);
            color: #fff;
            &:hover {
                background-color: var(--primary-600);
            }
        }

        &.spl-group-no-add,   // apply this to top-level, then button is hidden
        &.spl-flowform-disabled {  // or when disabled 
            .formulate-input-group-add-more {
                display: none;
            }
            .formulate-input-group-repeatable-remove {
                // this class is applied to an <a role="button"> with the remove icon 
                display: none;
            }
        }
    }

    // REPEATABLE GROUPS (sub-items)
    // NOTE - you can add custom classes 'in-parallel' with this by using props=dict(group_repeatable_class=['mycustomclass']) on nested/group fields!
    .formulate-input-group-repeatable {
        // removed these -- add this to nested/group field def props:  props=dict(group_repeatable_class=['d-flex', 'flex-wrap','gap-3'])
        // display: flex;
        // gap: 1rem;  // we will keep display:flex; and flex-wrap:wrap; for now - but for all spacing, put classes on inividual inputs in the group
        // flex-wrap: wrap;
        // we leave these other ones on, since they are more external to the field layout
        margin-bottom: 0.5rem;
        border: 1px solid var(--gray-300);
        border-radius: 0.5rem;
        background-color: var(--gray-200);

        .spl-flowform-item {  // the inputs INSIDE each group item
            min-width: 100px; // should likely be much bigger, but at least 100px... why? seems reasonable for now
            margin-bottom: 0;
            flex-grow: 1;
            &[data-type=hidden] {
                display: none;  // will value still be there?  we need it for ids, etc.
            }
        }
    }

    

    &[data-type=radio].inline-radios,
    &[data-type=checkbox].inline-checkboxes {  // a custom class we optionally add for radios OR checkbox groups (ie: multiple checkboxes)
        .formulate-input-element {  // this is the wrapper around the individual radios/checkboxes
            display: flex;
            row-gap: 0;
            column-gap: 1rem;
            flex-wrap: wrap;
        }
    }

    &[data-type=radio].twocol-radios,
    &[data-type=checkbox].twocol-checkboxes {  // a custom class we optionally add for radios OR checkbox groups (ie: multiple checkboxes)
        .formulate-input-element {  // this is the wrapper around the individual radios/checkboxes
            column-count: 2;
        }
    }

    // &.formulate-input[data-classification=select] .formulate-input-element::before
    // these selectors show an extra caret for select/dropdown, even when disabled... need to figure out how to hide when disabled -- add a css class when disabled

    // KEEP THIS LAST-ish
    &[data-type=hidden] {
        label {
            display: none;
        }
        .formulate-input-element {
            margin-bottom: 0;  // normally has some
        }
    }

}


</style>